import React, { ReactElement } from 'react'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'
import cn from 'classnames'
import { ResponsiveLayout } from 'common/components/ResponsiveLayout_V2'
import { ROUTES } from 'common/constants'
import { useMobileMediaQuery } from 'common/hooks/mediaQuery'
import { ChildSafetyIcon } from 'common/icons/ChildSafetyIcon'
import { PrivacyIcon } from 'common/icons/PrivacyIcon'
import { TermsIcon } from 'common/icons/TermsIcon'
import { Typography, TypographyVariants } from 'common/typography'
import { getAuth } from 'features/Auth/selectors'
import { PageHeader } from 'features/PageHeader_V2'
import { PolicyTab } from 'features/Policies/types'
import { selectOnboardingTranslations } from 'features/Translations/selectors'
import styles from './styles.module.sass'

interface IPolicies {
  children: ReactElement
}

export const Policies: React.FC<IPolicies> = ({ children }) => {
  const location = useLocation()
  const isInSinglePage = !location.pathname.includes(ROUTES.SETTINGS)
  const onboardingTranslations = useSelector(selectOnboardingTranslations)
  const isMobile = useMobileMediaQuery()
  const auth = useSelector(getAuth)

  const pathTabMap = {
    [`${ROUTES.PRIVACY_POLICY}`]: PolicyTab.Privacy,
    [`${ROUTES.TERMS_OF_SERVICE}`]: PolicyTab.Terms,
    [`${ROUTES.CHILD_SAFETY_POLICY}`]: PolicyTab.ChildSafety,
    [`${ROUTES.SETTINGS}${ROUTES.PRIVACY_POLICY}`]: PolicyTab.Privacy,
    [`${ROUTES.SETTINGS}${ROUTES.TERMS_OF_SERVICE}`]: PolicyTab.Terms,
    [`${ROUTES.SETTINGS}${ROUTES.CHILD_SAFETY_POLICY}`]: PolicyTab.ChildSafety
  }

  const currentTab = pathTabMap[location.pathname as keyof typeof pathTabMap]
  const getTabLink = (route: string) => (isInSinglePage ? route : `${ROUTES.SETTINGS}${route}`)

  const policyTabs = [
    {
      key: PolicyTab.Privacy,
      route: ROUTES.PRIVACY_POLICY,
      icon: PrivacyIcon,
      label: onboardingTranslations.policyPagePrivacyLinkText
    },
    {
      key: PolicyTab.Terms,
      route: ROUTES.TERMS_OF_SERVICE,
      icon: TermsIcon,
      label: onboardingTranslations.policyPageTermsLinkText
    },
    {
      key: PolicyTab.ChildSafety,
      route: ROUTES.CHILD_SAFETY_POLICY,
      icon: ChildSafetyIcon,
      label: onboardingTranslations.policyPageChildSafetyLinkText
    }
  ]

  const renderPolicyHeader = () => (
    <>
      {isMobile && !!auth
        ? <PageHeader pageName={onboardingTranslations.termsAndPoliciesTitle} className={styles.titleStyles} />
        : (
          isInSinglePage && !!auth && (
            <Typography variant={TypographyVariants.Desktop_UI_XXL_Medium} tag="span" className={styles.titleStyles}>
              {onboardingTranslations.termsAndPoliciesTitle}
            </Typography>
          )
        )}
      <div className={styles.tabs}>
        {policyTabs.map(({
          key, route, icon: Icon, label
        }) => (
          <Link
            key={key}
            className={cn(styles.tab, currentTab === key && styles.activeTab)}
            to={getTabLink(route)}
          >
            <Icon />
            <Typography variant={TypographyVariants.Desktop_UI_M_Medium} tag="span">
              {label}
            </Typography>
          </Link>
        ))}
      </div>
    </>
  )

  const getRightContent = () => (
    <div className={cn(styles.policiesContainer, !auth && styles.stylesOnPublicPage)}>
      {renderPolicyHeader()}
      {children}
    </div>
  )

  return (
    <ResponsiveLayout
      isLeftHiddenOnMobile
      className={cn(
        styles.termsAndConditionsRightContent,
        isInSinglePage && styles.inSinglePage
      )}
      rightContent={getRightContent()}
    />
  )
}
